/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  EmailTemplateRestService,
  filterSendFromToEnumDictionary,
  filterSystemRolesEnumDictionary,
  MilestoneEventFiresType,
  MilestoneEventFromToType,
  MilestoneEventRestService,
  MilestoneSendMethodType,
  SmsTemplateRestService,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  globalDictionaries,
  InitViewService,
  isRouteForCreationMode,
  TextDictionary,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { combineLatest$, of$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  EmailTemplateAutocompleteListItem,
  MILESTONE_EVENT_DETAILS_INIT,
  MilestoneEventDetails,
  milestoneEventFromDto,
  milestoneEventToDto,
} from './milestone-event-details.model';
import { sortByStringField } from '@gh/core-util';

@Injectable()
export class MilestoneEventDetailsBusinessService implements InitViewService {
  constructor(private restService: MilestoneEventRestService,
              private emailTemplateRestService: EmailTemplateRestService,
              private smsTemplateRest: SmsTemplateRestService,
              private dictionaryService: DictionaryService) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    return this.dictionaryService.getTextDictionaryFromGlobal(globalDictionaries.MilestoneEvents)
      .pipe(switchMap$((milestoneEventType) => combineLatest$([
                              this.smsTemplateRest.findAllAutocompleteItems()
                                .pipe(map$((smsTemplateList) => sortByStringField(smsTemplateList, 'name'))),
                              this.getEmailTemplatesList().pipe(map$((emailTemplateList) => sortByStringField(emailTemplateList, 'name'))),
                              this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesEnumDictionary(
                                ['Franchisee', 'Estimator', 'Supervisor', 'JobAdmin', 'Accountant', 'Salesperson']))),
                              this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesEnumDictionary(
                                ['Estimator', 'Supervisor', 'JobAdmin', 'Salesperson']))),
                              this.dictionaryService.getByEnum(UserSystemRole),
                              isRouteForCreationMode(route)
                                ? of$(MILESTONE_EVENT_DETAILS_INIT)
                                : this.get(getRouteParam(route, 'milestoneEventId')),
                              this.dictionaryService.getByEnum(MilestoneEventFromToType).pipe(
                                map$(filterSendFromToEnumDictionary(['GroupSystemRole', 'IndSystemRole']))),
                              this.dictionaryService.getByEnum(MilestoneEventFromToType),
                              this.dictionaryService.getByEnum(MilestoneEventFiresType),
                              this.dictionaryService.getByEnum(MilestoneSendMethodType),
                            ]).pipe(map$(([
                                                         smsTemplateList,
                                                         emailTemplateList,
                                                         groupSystemRoles,
                                                         indSystemRoles,
                                                         systemRoles,
                                                         milestoneEvent,
                                                         sendFromTypes,
                                                         sendToTypes,
                                                         milestoneEventFiresTypes,
                                                         milestoneSendMethodTypes,
                                                       ]) => ({
        smsTemplateList,
        emailTemplateList,
        groupSystemRoles,
        indSystemRoles,
        systemRoles,
        milestoneEvent,
        sendFromTypes,
        sendToTypes,
        milestoneEventFiresTypes,
        milestoneSendMethodTypes,
        milestoneEventType,
      }))),
    ));
  }

  get(milestoneEventId: ID): Observable<MilestoneEventDetails> {
    return this.dictionaryService.getTextDictionaryFromGlobal(globalDictionaries.MilestoneEvents)
      .pipe(switchMap$((milestoneEventType) => this.restService.get(milestoneEventId).pipe(
      map$(milestoneEventFromDto(milestoneEventType)))));
  }

  save(milestoneEvent: MilestoneEventDetails): Observable<MilestoneEventDetails> {
    return this.dictionaryService.getTextDictionaryFromGlobal(globalDictionaries.MilestoneEvents)
      .pipe(switchMap$((milestoneEventType) => milestoneEvent.milestoneEventId
        ? this.restService.update(milestoneEventToDto(milestoneEvent)).pipe(map$(milestoneEventFromDto(milestoneEventType)))
        : this.restService.create(milestoneEventToDto(milestoneEvent)).pipe(map$(milestoneEventFromDto(milestoneEventType)))));
  }

  getEmailTemplatesList(): Observable<EmailTemplateAutocompleteListItem[]> {
    return this.emailTemplateRestService.getAutocompleteList();
  }
}
